import type { AgentSdkMessage } from "@forgepoint/shared";
import { useItemsStore } from "@/modules/items/itemsStore";
import { useRecordQuotesStore } from "@/modules/quotes/quotesStore";
import { useQuoteDocumentsStore } from "@/modules/quoteDocuments/quoteDocumentsStore";
import {
  extractTextFromContent,
  isToolCallMessage,
  isToolCallResultMessage,
} from "@/modules/standardAgent/components/utils";

type RefreshEntityType = "item" | "quote" | "quoteDocument";

/** Agent tool names that mutate an entity, mapped to the store that owns it. */
const TOOL_ENTITY_TYPES: Record<string, RefreshEntityType> = {
  create_item_action: "item",
  update_item_action: "item",
  create_quote_action: "quote",
  update_quote_action: "quote",
  update_quote_document_action: "quoteDocument",
  create_quote_revision_action: "quote",
  finalize_quote_action: "quote",
  validate_quote_action: "quote",
};

const processedCallIdsByThread = new Map<string, Set<string>>();

/**
 * Refetches entities the agent just mutated so open views stay in sync.
 * Scans newly polled messages for completed mutation tool calls and triggers
 * a refetch-by-id in the owning store; tool results are never written into
 * stores directly. Each tool call is processed once on success; a failed
 * refetch un-marks its call_id so the next delivery that includes the result
 * (e.g. a full message fetch of the thread) retries.
 */
export function refreshEntitiesForToolResults(
  threadId: string,
  newMessages: AgentSdkMessage[],
  allMessages: AgentSdkMessage[],
): void {
  const processedCallIds = getProcessedCallIds(threadId);

  for (const message of newMessages) {
    const inner = message.message;
    if (!isToolCallResultMessage(inner)) {
      continue;
    }
    const entityType = TOOL_ENTITY_TYPES[inner.name];
    if (!entityType || processedCallIds.has(inner.call_id)) {
      continue;
    }

    const resultText = extractTextFromContent(inner.result);
    if (resultText.startsWith("Error:")) {
      processedCallIds.add(inner.call_id);
      continue;
    }

    const entityId =
      extractEntityIdFromResult(resultText, entityType) ??
      extractEntityIdFromCallArguments(inner.call_id, entityType, allMessages);
    if (!entityId) {
      continue;
    }

    processedCallIds.add(inner.call_id);
    refetchEntity(entityType, entityId).then((succeeded) => {
      if (!succeeded) {
        processedCallIds.delete(inner.call_id);
      }
    });
  }
}

/**
 * Marks the completed tool-call results in `messages` as processed without
 * refetching. Call when history loads (first fetch, older pages), so only
 * results that arrive after it trigger refetches.
 */
export function seedProcessedToolResults(
  threadId: string,
  messages: AgentSdkMessage[],
): void {
  const processedCallIds = getProcessedCallIds(threadId);
  for (const message of messages) {
    const inner = message.message;
    if (isToolCallResultMessage(inner)) {
      processedCallIds.add(inner.call_id);
    }
  }
}

function getProcessedCallIds(threadId: string): Set<string> {
  const existing = processedCallIdsByThread.get(threadId);
  if (existing) {
    return existing;
  }
  const created = new Set<string>();
  processedCallIdsByThread.set(threadId, created);
  return created;
}

async function refetchEntity(
  entityType: RefreshEntityType,
  entityId: string,
): Promise<boolean> {
  if (entityType === "item") {
    return useItemsStore.getState().fetchItem(entityId, true);
  }
  if (entityType === "quoteDocument") {
    const [quote, document] = await Promise.all([
      useRecordQuotesStore.getState().getQuote(entityId),
      useQuoteDocumentsStore.getState().fetchDocument(entityId),
    ]);
    return quote !== null && document !== null;
  }
  return (await useRecordQuotesStore.getState().getQuote(entityId)) !== null;
}

function extractEntityIdFromResult(
  resultText: string,
  entityType: RefreshEntityType,
): string | null {
  const parsed = safeJsonParse(resultText);
  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }
  const entity = (parsed as Record<string, unknown>)[entityType];
  if (typeof entity !== "object" || entity === null) {
    return null;
  }
  const id = (entity as Record<string, unknown>).id;
  return typeof id === "string" ? id : null;
}

function extractEntityIdFromCallArguments(
  callId: string,
  entityType: RefreshEntityType,
  allMessages: AgentSdkMessage[],
): string | null {
  const argumentsKey = entityType === "item" ? "id" : "quoteId";
  for (const message of allMessages) {
    const inner = message.message;
    if (!isToolCallMessage(inner) || inner.call_id !== callId) {
      continue;
    }
    const value = inner.arguments[argumentsKey];
    return typeof value === "string" ? value : null;
  }
  return null;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
